import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const ChannelManagerOperations = () => {
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('bookings');

  // Bookings State
  const [bookings, setBookings] = useState([]);
  const [bookingFilters, setBookingFilters] = useState({
    dateType: 'booked_on',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    status: 'all',
    channel: 'all',
    resNo: ''
  });
  const [bookingStats, setBookingStats] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    roomNights: 0,
    avgDailyRate: 0,
    avgLengthStay: 0,
    totalCancelled: 0
  });

  // Rates State
  const [ratesData, setRatesData] = useState([]);
  const [ratesFilter, setRatesFilter] = useState({ rate: '', roomType: 'all' });
  const [ratesDate, setRatesDate] = useState(new Date());
  const [roomTypes, setRoomTypes] = useState([]);

  // Availability State
  const [availability, setAvailability] = useState([]);
  const [availDate, setAvailDate] = useState(new Date());
  const [availFilter, setAvailFilter] = useState('all');

  // Bulk Update State
  const [bulkUpdate, setBulkUpdate] = useState({
    roomType: '',
    type: 'availability',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    value: '',
    days: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true
    }
  });

  // OTA Rule Set State
  const [channels, setChannels] = useState([]);
  const [ruleSet, setRuleSet] = useState({
    channelId: '',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    roomTypeId: '',
    type: 'rate',
    cpPlanId: '',
    epPlanId: '',
    cpRate: '',
    epRate: ''
  });
  const [savedRuleSets, setSavedRuleSets] = useState([]);

  // Color palette
  const colors = {
    primary: '#4F46E5',
    primaryDark: '#4338CA',
    secondary: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
    gray: {
      50: '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827'
    },
    white: '#FFFFFF'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchTabData();
  }, [activeTab]);

  const fetchInitialData = async () => {
    try {
      // Fetch channels
      const channelsData = await apiGet('/channel-manager/channels');
      if (channelsData.success) setChannels(channelsData.data);

      // Fetch room types
      const roomTypesData = await apiGet('/channel-manager/room-types');
      if (roomTypesData.success) setRoomTypes(roomTypesData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'bookings') {
        await fetchBookings();
      } else if (activeTab === 'availability') {
        await fetchAvailability();
      } else if (activeTab === 'rates') {
        await fetchRates();
      } else if (activeTab === 'ota-rule-set') {
        await fetchRuleSets();
      }
    } catch (error) {
      console.error('Error fetching tab data:', error);
    }
    setLoading(false);
  };

  const fetchBookings = async () => {
    try {
      const params = new URLSearchParams({
        dateType: bookingFilters.dateType,
        fromDate: bookingFilters.fromDate,
        toDate: bookingFilters.toDate,
        status: bookingFilters.status,
        channel: bookingFilters.channel,
        resNo: bookingFilters.resNo
      });

      const [bookingsRes, statsRes] = await Promise.all([
        fetch(`${PRODUCTION_URL}/api/channel-manager/bookings?${params}`),
        fetch(`${PRODUCTION_URL}/api/channel-manager/bookings/stats?${params}`)
      ]);

      const bookingsData = await bookingsRes.json();
      const statsData = await statsRes.json();

      if (bookingsData.success) setBookings(bookingsData.data);
      if (statsData.success) setBookingStats(statsData.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchRates = async () => {
    try {
      const startDate = new Date(ratesDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 10);

      const params = new URLSearchParams({
        fromDate: startDate.toISOString().split('T')[0],
        toDate: endDate.toISOString().split('T')[0],
        roomType: ratesFilter.roomType
      });

      const data = await apiGet('/channel-manager/rates?${params}');
      if (data.success) setRatesData(data.data);
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const startDate = new Date(availDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 10);

      const params = new URLSearchParams({
        fromDate: startDate.toISOString().split('T')[0],
        toDate: endDate.toISOString().split('T')[0],
        roomType: availFilter
      });

      const data = await apiGet('/channel-manager/availability?${params}');
      if (data.success) setAvailability(data.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchRuleSets = async () => {
    try {
      const data = await apiGet('/channel-manager/rule-sets');
      if (data.success) setSavedRuleSets(data.data);
    } catch (error) {
      console.error('Error fetching rule sets:', error);
    }
  };

  const handleBulkUpdate = async () => {
    if (!bulkUpdate.roomType) {
      alert('Please select a room type');
      return;
    }

    const selectedDays = Object.keys(bulkUpdate.days).filter(day => bulkUpdate.days[day]);
    if (selectedDays.length === 0) {
      alert('Please select at least one day');
      return;
    }

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/availability/bulk-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomTypeId: bulkUpdate.roomType,
          fromDate: bulkUpdate.fromDate,
          toDate: bulkUpdate.toDate,
          updateType: bulkUpdate.type,
          value: bulkUpdate.value,
          days: selectedDays
        })
      });

      const data = await res.json();
      if (data.success) {
        alert(`✅ ${data.message}`);
        fetchTabData();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error bulk updating:', error);
      alert('❌ Error performing bulk update');
    }
  };

  const handleSaveRuleSet = async () => {
    if (!ruleSet.channelId || !ruleSet.roomTypeId) {
      alert('Please select channel and room type');
      return;
    }

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/rule-sets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ruleSet)
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Rule set created successfully');
        setRuleSet({
          channelId: '',
          fromDate: new Date().toISOString().split('T')[0],
          toDate: new Date().toISOString().split('T')[0],
          roomTypeId: '',
          type: 'rate',
          cpPlanId: '',
          epPlanId: '',
          cpRate: '',
          epRate: ''
        });
        fetchRuleSets();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error saving rule set:', error);
      alert('❌ Error saving rule set');
    }
  };

  const handleDeleteRuleSet = async (id) => {
    if (!window.confirm('Are you sure you want to clear this rule set?')) return;

    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/rule-sets/${id}`, {
        method: 'DELETE'
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Rule set cleared successfully');
        fetchRuleSets();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting rule set:', error);
      alert('❌ Error clearing rule set');
    }
  };

  const handleUpdateRate = async (rateId, newAmount) => {
    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/rates/${rateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate_amount: newAmount })
      });

      const data = await res.json();
      if (data.success) {
        console.log('Rate updated');
      }
    } catch (error) {
      console.error('Error updating rate:', error);
    }
  };

  const handleUpdateAvailability = async (availId, field, value) => {
    try {
      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/availability/${availId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value })
      });

      const data = await res.json();
      if (data.success) {
        console.log('Availability updated');
      }
    } catch (error) {
      console.error('Error updating availability:', error);
    }
  };

  const handleCopyExceedInventory = async (roomTypeId) => {
    try {
      const startDate = new Date(availDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 10);

      const res = await fetch(`${PRODUCTION_URL}/api/channel-manager/availability/copy-exceed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomTypeId,
          fromDate: startDate.toISOString().split('T')[0],
          toDate: endDate.toISOString().split('T')[0]
        })
      });

      const data = await res.json();
      if (data.success) {
        alert('✅ Exceed inventory copied to OTA');
        fetchAvailability();
      } else {
        alert('❌ ' + data.message);
      }
    } catch (error) {
      console.error('Error copying inventory:', error);
      alert('❌ Error copying inventory');
    }
  };

  // Common styles
  const styles = {
    container: {
      padding: isMobile ? '16px' : '24px',
      maxWidth: '1600px',
      margin: '0 auto'
    },
    header: {
      marginBottom: isMobile ? '20px' : '32px'
    },
    title: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '700',
      color: colors.gray[900],
      marginBottom: '8px'
    },
    subtitle: {
      fontSize: isMobile ? '14px' : '16px',
      color: colors.gray[600]
    },
    tabsContainer: {
      display: 'flex',
      gap: '8px',
      marginBottom: isMobile ? '16px' : '24px',
      overflowX: 'auto',
      paddingBottom: '8px',
      borderBottom: `2px solid ${colors.gray[200]}`
    },
    tab: (active) => ({
      padding: isMobile ? '10px 16px' : '12px 24px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      color: active ? colors.primary : colors.gray[600],
      background: 'transparent',
      border: 'none',
      borderBottom: active ? `3px solid ${colors.primary}` : '3px solid transparent',
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap'
    }),
    card: {
      background: colors.white,
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: isMobile ? '16px' : '24px',
      marginBottom: '16px'
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 24px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: colors.primary,
      color: colors.white
    },
    input: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      fontSize: isMobile ? '13px' : '14px',
      border: `1px solid ${colors.gray[300]}`,
      borderRadius: '8px',
      width: '100%',
      outline: 'none'
    },
    select: {
      padding: isMobile ? '10px 12px' : '12px 16px',
      fontSize: isMobile ? '13px' : '14px',
      border: `1px solid ${colors.gray[300]}`,
      borderRadius: '8px',
      width: '100%',
      outline: 'none',
      background: colors.white,
      cursor: 'pointer'
    }
  };

  // Stat Card Component
  const StatCard = ({ icon, label, value, color }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: `1px solid ${colors.gray[200]}`
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <span style={{ fontSize: '24px' }}>{icon}</span>
        <div style={{ fontSize: isMobile ? '11px' : '12px', color: colors.gray[600], fontWeight: '600', textTransform: 'uppercase' }}>
          {label}
        </div>
      </div>
      <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', color }}>
        {value}
      </div>
    </div>
  );

  // Render Bookings Tab
  const renderBookingsTab = () => (
    <div>
      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <StatCard icon="💵" label="Total Revenue" value={`₹${bookingStats.totalRevenue?.toLocaleString() || 0}`} color={colors.secondary} />
        <StatCard icon="📊" label="Total Bookings" value={bookingStats.totalBookings || 0} color={colors.warning} />
        <StatCard icon="🌙" label="Room Nights" value={bookingStats.roomNights || 0} color="#EC4899" />
        <StatCard icon="💰" label="Avg. Daily Rate" value={`₹${bookingStats.avgDailyRate?.toLocaleString() || 0}`} color="#3B82F6" />
        <StatCard icon="📅" label="Avg. Length of Stay" value={bookingStats.avgLengthStay || 0} color="#F97316" />
        <StatCard icon="❌" label="Total Cancelled" value={bookingStats.totalCancelled || 0} color={colors.danger} />
      </div>

      {/* Filters */}
      <div style={styles.card}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Date
            </label>
            <select
              style={styles.select}
              value={bookingFilters.dateType}
              onChange={(e) => setBookingFilters({ ...bookingFilters, dateType: e.target.value })}
            >
              <option value="booked_on">Booked On Date</option>
              <option value="arrival">Arrival Date</option>
              <option value="departure">Departure Date</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              From
            </label>
            <input
              type="date"
              style={styles.input}
              value={bookingFilters.fromDate}
              onChange={(e) => setBookingFilters({ ...bookingFilters, fromDate: e.target.value })}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              To
            </label>
            <input
              type="date"
              style={styles.input}
              value={bookingFilters.toDate}
              onChange={(e) => setBookingFilters({ ...bookingFilters, toDate: e.target.value })}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Status
            </label>
            <select
              style={styles.select}
              value={bookingFilters.status}
              onChange={(e) => setBookingFilters({ ...bookingFilters, status: e.target.value })}
            >
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              OTA Channels
            </label>
            <select
              style={styles.select}
              value={bookingFilters.channel}
              onChange={(e) => setBookingFilters({ ...bookingFilters, channel: e.target.value })}
            >
              <option value="all">All OTA Channels</option>
              {channels.map(ch => (
                <option key={ch.id} value={ch.channel_code}>{ch.channel_name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              OTA Res. No.
            </label>
            <input
              type="text"
              style={styles.input}
              placeholder="Search by Res. No."
              value={bookingFilters.resNo}
              onChange={(e) => setBookingFilters({ ...bookingFilters, resNo: e.target.value })}
            />
          </div>
        </div>

        <button style={styles.button} onClick={fetchBookings}>
          Search
        </button>
      </div>

      {/* Bookings List */}
      <div style={styles.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '14px' }}>
            <thead>
              <tr style={{ background: colors.gray[50], borderBottom: `2px solid ${colors.gray[200]}` }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Guest Name</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Exceed Res. No.</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Rooms</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Arrival</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Departure</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Booked On</th>
                <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: colors.gray[500] }}>
                    No bookings found
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => (
                  <tr key={booking.id} style={{ borderBottom: `1px solid ${colors.gray[100]}` }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#EF4444',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '12px'
                        }}>
                          {booking.channel_name?.substring(0, 2).toUpperCase() || 'OT'}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: colors.gray[900] }}>{booking.guest_name}</div>
                          <div style={{ fontSize: '12px', color: colors.secondary, marginTop: '2px' }}>{booking.booking_status}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: '600', color: colors.primary }}>{booking.exceed_res_no || 'N/A'}</div>
                      <div style={{ fontSize: '12px', color: colors.gray[500], marginTop: '2px' }}>
                        OTA: {booking.channel_booking_id}
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <span style={{
                        background: colors.warning,
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontWeight: '600',
                        fontSize: '18px'
                      }}>
                        {booking.number_of_rooms}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ color: colors.secondary }}>
                        <div style={{ fontWeight: '600' }}>{new Date(booking.check_in_date).toLocaleString('default', { month: 'short' })}</div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>{new Date(booking.check_in_date).getDate()}</div>
                        <div style={{ fontSize: '12px' }}>{new Date(booking.check_in_date).getFullYear()}</div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ color: '#EC4899' }}>
                        <div style={{ fontWeight: '600' }}>{new Date(booking.check_out_date).toLocaleString('default', { month: 'short' })}</div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>{new Date(booking.check_out_date).getDate()}</div>
                        <div style={{ fontSize: '12px' }}>{new Date(booking.check_out_date).getFullYear()}</div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ color: colors.primary }}>
                        <div style={{ fontWeight: '600' }}>{new Date(booking.booked_on_date).toLocaleString('default', { month: 'short' })}</div>
                        <div style={{ fontSize: '20px', fontWeight: '700' }}>{new Date(booking.booked_on_date).getDate()}</div>
                        <div style={{ fontSize: '12px' }}>{new Date(booking.booked_on_date).getFullYear()}</div>
                      </div>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: colors.gray[900] }}>
                        ₹{parseFloat(booking.total_amount).toLocaleString()}
                      </div>
                      <div style={{
                        display: 'inline-block',
                        marginTop: '4px',
                        background: colors.primary,
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {booking.payment_status}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render Rates Tab
  const renderRatesTab = () => (
    <div>
      {/* Filters */}
      <div style={styles.card}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Room Type
            </label>
            <select
              style={styles.select}
              value={ratesFilter.roomType}
              onChange={(e) => setRatesFilter({ ...ratesFilter, roomType: e.target.value })}
            >
              <option value="all">All Room Types</option>
              {roomTypes.map(rt => (
                <option key={rt.id} value={rt.id}>{rt.room_type_name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Date
            </label>
            <input
              type="date"
              style={styles.input}
              value={ratesDate.toISOString().split('T')[0]}
              onChange={(e) => setRatesDate(new Date(e.target.value))}
            />
          </div>

          <button style={styles.button} onClick={fetchRates}>Search</button>
          <button style={{ ...styles.button, background: colors.secondary }} onClick={() => alert('Rates will be saved automatically on blur')}>
            Save
          </button>
        </div>
      </div>

      {/* Rate Calendar */}
      <div style={styles.card}>
        <div style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
          Rate Calendar
        </div>
        <div style={{ overflowX: 'auto' }}>
          <p style={{ fontSize: '14px', color: colors.gray[600], marginBottom: '16px' }}>
            Showing rates for next 10 days. Edit rates directly and they will save automatically.
          </p>
          {ratesData.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.gray[500] }}>
              No rate data available. Please generate rates first.
            </div>
          ) : (
            <div style={{ fontSize: '13px' }}>
              {Object.entries(
                ratesData.reduce((acc, rate) => {
                  const key = `${rate.room_type_name}_${rate.plan_name}`;
                  if (!acc[key]) acc[key] = { roomType: rate.room_type_name, planName: rate.plan_name, rates: [] };
                  acc[key].rates.push(rate);
                  return acc;
                }, {})
              ).map(([key, group]) => (
                <div key={key} style={{ marginBottom: '24px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px', background: colors.gray[100], padding: '8px', borderRadius: '6px' }}>
                    {group.roomType} - {group.planName}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {group.rates.map((rate) => (
                      <div key={rate.id} style={{ minWidth: '120px', border: `1px solid ${colors.gray[300]}`, borderRadius: '6px', padding: '12px' }}>
                        <div style={{ fontSize: '11px', color: colors.gray[600], marginBottom: '4px' }}>
                          {new Date(rate.rate_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div style={{ fontSize: '11px', color: colors.gray[600], marginBottom: '8px' }}>
                          👤 {rate.pax} pax
                        </div>
                        <input
                          type="number"
                          value={rate.rate_amount}
                          onChange={(e) => {
                            const newData = ratesData.map(r =>
                              r.id === rate.id ? { ...r, rate_amount: e.target.value } : r
                            );
                            setRatesData(newData);
                          }}
                          onBlur={(e) => handleUpdateRate(rate.id, e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: `1px solid ${colors.gray[300]}`,
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '600'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render Availability Tab
  const renderAvailabilityTab = () => (
    <div>
      {/* Warning Banner */}
      <div style={{
        background: '#FEF2F2',
        border: `1px solid ${colors.danger}`,
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '16px',
        color: colors.danger,
        fontSize: '14px'
      }}>
        *Cannot update OTA inventory manually as it is linked with Exceed inventory. To block any date use stop sell checkbox.
      </div>

      {/* Filters */}
      <div style={styles.card}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Room Type
            </label>
            <select style={styles.select} value={availFilter} onChange={(e) => setAvailFilter(e.target.value)}>
              <option value="all">All Room Types</option>
              {roomTypes.map(rt => (
                <option key={rt.id} value={rt.id}>{rt.room_type_name}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Date
            </label>
            <input
              type="date"
              style={styles.input}
              value={availDate.toISOString().split('T')[0]}
              onChange={(e) => setAvailDate(new Date(e.target.value))}
            />
          </div>

          <button style={styles.button} onClick={fetchAvailability}>Search</button>
          <button style={{ ...styles.button, background: colors.secondary }} onClick={() => alert('Changes saved automatically')}>
            Save
          </button>
        </div>
      </div>

      {/* Availability Calendar */}
      <div style={styles.card}>
        <div style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
          Availability Calendar
        </div>
        <div style={{ overflowX: 'auto' }}>
          {availability.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: colors.gray[500] }}>
              No availability data. Please initialize availability first.
            </div>
          ) : (
            <div style={{ fontSize: '13px' }}>
              {Object.entries(
                availability.reduce((acc, avail) => {
                  if (!acc[avail.room_type_name]) acc[avail.room_type_name] = [];
                  acc[avail.room_type_name].push(avail);
                  return acc;
                }, {})
              ).map(([roomType, avails]) => (
                <div key={roomType} style={{ marginBottom: '32px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '12px', background: colors.gray[100], padding: '12px', borderRadius: '6px' }}>
                    {roomType}
                  </div>

                  {/* Exceed Inventory Row */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: '500', marginBottom: '8px', fontSize: '12px' }}>Available in Exceed:</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {avails.map((avail) => (
                        <div key={`exceed-${avail.id}`} style={{ minWidth: '100px' }}>
                          <div style={{ fontSize: '10px', color: colors.gray[600], marginBottom: '4px' }}>
                            {new Date(avail.availability_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                          <input
                            type="number"
                            value={avail.available_exceed}
                            onChange={(e) => {
                              const newData = availability.map(a =>
                                a.id === avail.id ? { ...a, available_exceed: e.target.value } : a
                              );
                              setAvailability(newData);
                            }}
                            onBlur={(e) => handleUpdateAvailability(avail.id, 'available_exceed', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: `2px solid ${avail.available_exceed == 0 ? colors.danger : colors.gray[300]}`,
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: avail.available_exceed == 0 ? '#FEE2E2' : 'white'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* OTA Inventory Row */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <div style={{ fontWeight: '500', fontSize: '12px' }}>Available in OTA:</div>
                      <label style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              handleCopyExceedInventory(avails[0].room_type_id);
                            }
                          }}
                        />
                        Copy Exceed Inventory
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {avails.map((avail) => (
                        <div key={`ota-${avail.id}`} style={{ minWidth: '100px' }}>
                          <div style={{ fontSize: '10px', color: colors.gray[600], marginBottom: '4px' }}>
                            {new Date(avail.availability_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                          <input
                            type="number"
                            value={avail.available_ota}
                            readOnly
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: `2px solid ${colors.secondary}`,
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontSize: '14px',
                              fontWeight: '600',
                              background: '#D1FAE5',
                              color: colors.secondary
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stop Sell Row */}
                  <div>
                    <div style={{ fontWeight: '500', marginBottom: '8px', fontSize: '12px' }}>Stop Sell:</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {avails.map((avail) => (
                        <div key={`stop-${avail.id}`} style={{ minWidth: '100px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={avail.stop_sell == 1}
                            onChange={(e) => handleUpdateAvailability(avail.id, 'stop_sell', e.target.checked ? 1 : 0)}
                            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render Bulk Update Tab
  const renderBulkUpdateTab = () => (
    <div style={styles.card}>
      <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '24px', color: colors.gray[900] }}>
        Bulk Update
      </h2>

      <div style={{ maxWidth: '800px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
            Room Type
          </label>
          <select
            style={styles.select}
            value={bulkUpdate.roomType}
            onChange={(e) => setBulkUpdate({ ...bulkUpdate, roomType: e.target.value })}
          >
            <option value="">Select</option>
            {roomTypes.map(rt => (
              <option key={rt.id} value={rt.id}>{rt.room_type_name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
            Type
          </label>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {['availability', 'stop_sell'].map((type) => (
              <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="updateType"
                  value={type}
                  checked={bulkUpdate.type === type}
                  onChange={(e) => setBulkUpdate({ ...bulkUpdate, type: e.target.value })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', color: colors.gray[700], textTransform: 'capitalize' }}>
                  {type.replace('_', ' ')}
                </span>
              </label>
            ))}
          </div>
        </div>

        {bulkUpdate.type === 'availability' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Value
            </label>
            <input
              type="number"
              style={styles.input}
              value={bulkUpdate.value}
              onChange={(e) => setBulkUpdate({ ...bulkUpdate, value: e.target.value })}
              placeholder="Enter number of rooms"
            />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              From
            </label>
            <input
              type="date"
              style={styles.input}
              value={bulkUpdate.fromDate}
              onChange={(e) => setBulkUpdate({ ...bulkUpdate, fromDate: e.target.value })}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              To
            </label>
            <input
              type="date"
              style={styles.input}
              value={bulkUpdate.toDate}
              onChange={(e) => setBulkUpdate({ ...bulkUpdate, toDate: e.target.value })}
            />
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
            Select Days
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '12px' }}>
            {Object.keys(bulkUpdate.days).map((day) => (
              <label key={day} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px',
                background: bulkUpdate.days[day] ? colors.primary : colors.gray[100],
                color: bulkUpdate.days[day] ? 'white' : colors.gray[700],
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={bulkUpdate.days[day]}
                  onChange={(e) => setBulkUpdate({
                    ...bulkUpdate,
                    days: { ...bulkUpdate.days, [day]: e.target.checked }
                  })}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px', fontWeight: '500', textTransform: 'capitalize' }}>
                  {day}
                </span>
              </label>
            ))}
          </div>
        </div>

        <button style={{ ...styles.button, width: isMobile ? '100%' : 'auto' }} onClick={handleBulkUpdate}>
          Apply Bulk Update
        </button>
      </div>
    </div>
  );

  // Render OTA Rule Set Tab
  const renderOTARuleSetTab = () => (
    <div>
      <div style={styles.card}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: colors.gray[900] }}>
          Create Rule Set
        </h2>
        <p style={{ fontSize: '14px', color: colors.gray[600], marginBottom: '8px' }}>
          • You can control stop-sell and update rates for <strong>specific OTAs</strong> (on current rates)
        </p>
        <p style={{ fontSize: '14px', color: colors.gray[600], marginBottom: '24px' }}>
          • To remove the applied restrictions and conditions from OTA, click <strong>Clear Rule</strong> from the <strong>Saved Rule Sets</strong> section below
        </p>

        <div style={{ maxWidth: '600px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              OTA
            </label>
            <select
              style={styles.select}
              value={ruleSet.channelId}
              onChange={(e) => setRuleSet({ ...ruleSet, channelId: e.target.value })}
            >
              <option value="">Select OTA</option>
              {channels.map(ch => (
                <option key={ch.id} value={ch.id}>{ch.channel_name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
                Date From
              </label>
              <input
                type="date"
                style={styles.input}
                value={ruleSet.fromDate}
                onChange={(e) => setRuleSet({ ...ruleSet, fromDate: e.target.value })}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
                To
              </label>
              <input
                type="date"
                style={styles.input}
                value={ruleSet.toDate}
                onChange={(e) => setRuleSet({ ...ruleSet, toDate: e.target.value })}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
              Room Type
            </label>
            <select
              style={styles.select}
              value={ruleSet.roomTypeId}
              onChange={(e) => setRuleSet({ ...ruleSet, roomTypeId: e.target.value })}
            >
              <option value="">Select</option>
              {roomTypes.map(rt => (
                <option key={rt.id} value={rt.id}>{rt.room_type_name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button
                style={{
                  flex: 1,
                  padding: '12px',
                  background: ruleSet.type === 'rate' ? colors.primary : colors.gray[200],
                  color: ruleSet.type === 'rate' ? 'white' : colors.gray[700],
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={() => setRuleSet({ ...ruleSet, type: 'rate' })}
              >
                Rate
              </button>
              <button
                style={{
                  flex: 1,
                  padding: '12px',
                  background: ruleSet.type === 'stop_sell' ? colors.primary : colors.gray[200],
                  color: ruleSet.type === 'stop_sell' ? 'white' : colors.gray[700],
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={() => setRuleSet({ ...ruleSet, type: 'stop_sell' })}
              >
                Stop-Sell
              </button>
            </div>
          </div>

          {ruleSet.type === 'rate' && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
                  CP Plan Rate Adjustment
                </label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="Enter amount (+/-)"
                  value={ruleSet.cpRate}
                  onChange={(e) => setRuleSet({ ...ruleSet, cpRate: e.target.value })}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: colors.gray[700] }}>
                  EP Plan Rate Adjustment
                </label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="Enter amount (+/-)"
                  value={ruleSet.epRate}
                  onChange={(e) => setRuleSet({ ...ruleSet, epRate: e.target.value })}
                />
              </div>
            </div>
          )}

          <button style={{ ...styles.button, background: colors.secondary, width: isMobile ? '100%' : 'auto' }} onClick={handleSaveRuleSet}>
            Save Rule Set
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: colors.gray[900] }}>
          Saved Rule Sets
        </h2>

        {savedRuleSets.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: colors.gray[500],
            background: colors.gray[50],
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '14px' }}>No Record(s) Found.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: colors.gray[100], borderBottom: `2px solid ${colors.gray[300]}` }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Channel</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Room Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Date Range</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Rule Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Applied By</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {savedRuleSets.map((rule) => (
                  <tr key={rule.id} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                    <td style={{ padding: '12px' }}>{rule.channel_name}</td>
                    <td style={{ padding: '12px' }}>{rule.room_type_name}</td>
                    <td style={{ padding: '12px' }}>
                      {new Date(rule.from_date).toLocaleDateString()} - {new Date(rule.to_date).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        background: rule.rule_type === 'rate' ? '#DBEAFE' : '#FEE2E2',
                        color: rule.rule_type === 'rate' ? '#1E40AF' : '#991B1B'
                      }}>
                        {rule.rule_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{rule.applied_by_name || 'Admin'}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleDeleteRuleSet(rule.id)}
                        style={{
                          padding: '6px 16px',
                          background: colors.danger,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Clear Rule
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Channel Manager - Operations</h1>
          <p style={styles.subtitle}>Manage bookings, rates, availability, and OTA rules</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabsContainer}>
          <button style={styles.tab(activeTab === 'bookings')} onClick={() => setActiveTab('bookings')}>
            📅 Bookings
          </button>
          <button style={styles.tab(activeTab === 'availability')} onClick={() => setActiveTab('availability')}>
            📊 Availability
          </button>
          <button style={styles.tab(activeTab === 'rates')} onClick={() => setActiveTab('rates')}>
            💰 Rates
          </button>
          <button style={styles.tab(activeTab === 'bulk-update')} onClick={() => setActiveTab('bulk-update')}>
            📋 Bulk Update
          </button>
          <button style={styles.tab(activeTab === 'ota-rule-set')} onClick={() => setActiveTab('ota-rule-set')}>
            ⚙️ OTA Rule Set
          </button>
        </div>

        {/* Tab Content */}
        {loading ? (
          <div style={{
            ...styles.card,
            textAlign: 'center',
            padding: '60px 20px',
            color: colors.gray[500]
          }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>⏳</div>
            <div>Loading...</div>
          </div>
        ) : (
          <>
            {activeTab === 'bookings' && renderBookingsTab()}
            {activeTab === 'availability' && renderAvailabilityTab()}
            {activeTab === 'rates' && renderRatesTab()}
            {activeTab === 'bulk-update' && renderBulkUpdateTab()}
            {activeTab === 'ota-rule-set' && renderOTARuleSetTab()}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default ChannelManagerOperations;